import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Divider,
  Alert,
  Autocomplete,
  CircularProgress
} from '@mui/material';
import { Add, Delete, Save, Cancel } from '@mui/icons-material';
import { makeApiCall } from '../../config/api';
import { formatCurrencyWithSymbol } from '../../utils/currencyUtils';
import SiteDiagramUpload from './SiteDiagramUpload';

const defaultItem = () => ({ description: '', quantity: 1, unit: '', unit_price: 0, discount_percent: 0, item_type: 'custom', service_code: '' });

// TKT-470: units for construction lines. Stored as these codes; labels come from i18n.
// TKT-477: 'ton' added, plus a Custom choice that stores whatever the user types.
const UNIT_OPTIONS = ['yards', 'bags', 'each', 'sq_ft', 'hours', 'ton'];
const CUSTOM_UNIT = '__custom__';
const isCustomUnit = (it) => !!it._customUnit || (!!it.unit && !UNIT_OPTIONS.includes(it.unit));

const QuoteForm = ({ quote, onSuccess, onCancel }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lookups, setLookups] = useState({ serviceTypes: [], quoteTypes: [], propertyTypes: [], statuses: [] });
  const [items, setItems] = useState([defaultItem()]);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [customerOptions, setCustomerOptions] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [formData, setFormData] = useState({
    customer_id: '',
    quote_reference: '',
    service_type: '',
    quote_type_id: 3,
    description: '',
    property_type_id: '',
    sales_person_id: '',
    status: 'draft'
  });

  // init
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        const lookupRes = await makeApiCall('api/quotes/lookup');
        const lookupData = await lookupRes.json();
        if (!lookupRes.ok) throw new Error(lookupData.error || 'Failed to load lookups');
        setLookups(lookupData);

        if (quote) {
          setFormData({
            customer_id: quote.customer_id || '',
            quote_reference: quote.quote_reference || '',
            service_type: quote.service_type || '',
            quote_type_id: quote.quote_type_id || '',
            description: quote.description || '',
            property_type_id: quote.property_type_id || '',
            sales_person_id: quote.sales_person_id || '',
            status: quote.status || 'draft'
          });
          if (quote.items) setItems(quote.items);
          if (quote.customer_id) {
            const customerRes = await makeApiCall(`api/customers/${quote.customer_id}`);
            if (customerRes.ok) setSelectedCustomer(await customerRes.json());
          }
        }
      } catch (e) {
        setError('Failed to load metadata');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [quote]);

  // Server-side customer search (name, address, email, phone). Results are
  // shown as returned, and a newer keystroke invalidates the older request.
  const customerSearchRequest = useRef(0);
  useEffect(() => {
    if (!customerSearchTerm || customerSearchTerm.length < 2) {
      setCustomerOptions([]);
      return;
    }

    const requestId = ++customerSearchRequest.current;
    const timeoutId = setTimeout(async () => {
      setCustomerLoading(true);
      try {
        const res = await makeApiCall(`api/customers?search=${encodeURIComponent(customerSearchTerm)}&limit=50`);
        const data = await res.json();
        if (requestId !== customerSearchRequest.current) return;
        if (res.ok) setCustomerOptions(data.customers || data.data || []);
      } catch (e) {
        if (requestId !== customerSearchRequest.current) return;
        console.error('Failed to search customers:', e);
      } finally {
        if (requestId === customerSearchRequest.current) setCustomerLoading(false);
      }
    }, 300);

    return () => {
      clearTimeout(timeoutId);
      customerSearchRequest.current += 1;
    };
  }, [customerSearchTerm]);

  const getCustomerDisplayName = (customer) => (
    customer?.company_name ||
    `${customer?.first_name || ''} ${customer?.last_name || ''}`.trim() ||
    t('quotes.unnamedCustomer')
  );

  const getCustomerAddress = (customer) => [
    customer?.street_address,
    [customer?.city, customer?.province, customer?.postal_code].filter(Boolean).join(', ')
  ].filter(Boolean).join(' • ');

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleItemChange = (index, field, value) => {
    setItems(prev => prev.map((it, i) => i === index ? { ...it, [field]: value } : it));
  };

  const addItem = () => setItems(prev => [...prev, defaultItem()]);
  const removeItem = (index) => setItems(prev => prev.filter((_, i) => i !== index));

  // Qty x Unit Price less the line discount (TKT-438 shows it per line).
  const lineAmount = (it) => {
    const qty = Number(it.quantity) || 0;
    const unit = Number(it.unit_price) || 0;
    const discount = Number(it.discount_percent) || 0;
    let line = qty * unit;
    if (discount > 0) line = line * (1 - discount / 100);
    return Math.round(line * 100) / 100;
  };

  const computeTotals = () => {
    const subtotal = items.reduce((sum, it) => sum + lineAmount(it), 0);
    return { subtotal: Math.round(subtotal * 100) / 100 };
  };

  const onSubmit = async () => {
    setError('');
    if (!formData.customer_id) {
      setError(t('quotes.customerRequired'));
      return;
    }
    setLoading(true);
    try {
      // _customUnit is only a form flag (TKT-477); don't send it.
      const payload = { ...formData, items: items.map(({ _customUnit, ...rest }) => rest) };
      const method = quote ? 'PUT' : 'POST';
      const endpoint = quote ? `api/quotes/${quote.id}` : 'api/quotes';
      const res = await makeApiCall(endpoint, {
        method,
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Request failed');
      onSuccess && onSuccess(data.quote || true);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const { subtotal } = computeTotals();

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>{quote ? 'Edit Quote' : 'Create Quote'}</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <CircularProgress size={28} sx={{ mb: 2 }} />}

      <Box component="form" noValidate sx={{ width: '100%' }}>
        {/* Section: Quote Info */}
        <Box sx={{ p: 2, mb: 2, borderRadius: 2, border: '1px solid #eee', background: '#fff' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Quote Details</Typography>
          <Grid container spacing={2}>
            <Grid size={12}>
              <Autocomplete
                fullWidth
                options={customerOptions}
                value={selectedCustomer}
                onChange={(event, newValue) => {
                  setSelectedCustomer(newValue);
                  handleChange('customer_id', newValue?.id || '');
                }}
                inputValue={customerSearchTerm}
                onInputChange={(event, newInputValue) => setCustomerSearchTerm(newInputValue)}
                filterOptions={(options) => options}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                getOptionLabel={(option) => {
                  const name = getCustomerDisplayName(option);
                  const address = getCustomerAddress(option);
                  return address ? `${name} — ${address}` : name;
                }}
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={option.id}>
                    <Box sx={{ width: '100%' }}>
                      <Typography variant="body1" fontWeight="bold">{getCustomerDisplayName(option)}</Typography>
                      {getCustomerAddress(option) && (
                        <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>{getCustomerAddress(option)}</Typography>
                      )}
                      {option.email_address && (
                        <Typography variant="body2" color="textSecondary">{option.email_address}</Typography>
                      )}
                      {option.phone_number && (
                        <Typography variant="body2" color="textSecondary">{option.phone_number}</Typography>
                      )}
                    </Box>
                  </Box>
                )}
                loading={customerLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('quotes.searchCustomer')}
                    placeholder={t('quotes.searchCustomerPlaceholder')}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {customerLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                noOptionsText={
                  customerSearchTerm.length < 2
                    ? t('quotes.typeAtLeastTwoCharacters')
                    : customerLoading
                      ? t('quotes.searchingCustomers')
                      : t('quotes.noCustomersFound')
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel id="service-type-label">Service Type</InputLabel>
                <Select labelId="service-type-label" label="Service Type" value={formData.service_type} onChange={e => handleChange('service_type', e.target.value)}>
                  {lookups.serviceTypes.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel id="quote-type-label">Quote Type</InputLabel>
                <Select labelId="quote-type-label" label="Quote Type" value={formData.quote_type_id} onChange={e => handleChange('quote_type_id', e.target.value)}>
                  {lookups.quoteTypes.map(qt => <MenuItem key={qt.id} value={qt.id}>{qt.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel id="property-type-label">Property Type</InputLabel>
                <Select labelId="property-type-label" label="Property Type" value={formData.property_type_id} onChange={e => handleChange('property_type_id', e.target.value)}>
                  {(lookups.propertyTypes || []).map(pt => <MenuItem key={pt.id} value={pt.id}>{pt.type_name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={12}>
              <TextField fullWidth label="Reference" value={formData.quote_reference} onChange={e => handleChange('quote_reference', e.target.value)} />
            </Grid>
            <Grid size={12}>
              <TextField fullWidth label="Description" multiline minRows={3} value={formData.description} onChange={e => handleChange('description', e.target.value)} />
            </Grid>
          </Grid>
        </Box>

        {/* Section: Items */}
        <Box sx={{ p: 2, mb: 2, borderRadius: 2, border: '1px solid #eee', background: '#fff' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>{t('quoteForm.items.title')}</Typography>
          {items.map((it, idx) => (
            <Grid container spacing={2} key={idx} alignItems="center" sx={{ mb: 2, pb: 2, borderBottom: idx < items.length - 1 ? '1px solid #eee' : 'none' }}>
              <Grid size={{ xs: 10, sm: 11 }}>
                <TextField fullWidth label={t('quoteForm.items.description')} value={it.description} onChange={e => handleItemChange(idx, 'description', e.target.value)} />
              </Grid>
              <Grid size={{ xs: 2, sm: 1 }}>
                <Box display="flex" justifyContent="flex-end">
                  <IconButton aria-label={t('quoteForm.items.remove')} onClick={() => removeItem(idx)}><Delete /></IconButton>
                </Box>
              </Grid>
              <Grid size={{ xs: 4, sm: 2 }}>
                <TextField fullWidth type="number" label={t('quoteForm.items.qty')} value={it.quantity} onChange={e => handleItemChange(idx, 'quantity', e.target.value)} />
              </Grid>
              <Grid size={{ xs: 4, sm: 2 }}>
                <TextField
                  fullWidth
                  select
                  label={t('quoteForm.items.unit')}
                  value={isCustomUnit(it) ? CUSTOM_UNIT : (it.unit || '')}
                  onChange={e => {
                    const v = e.target.value;
                    setItems(prev => prev.map((row, i) => {
                      if (i !== idx) return row;
                      if (v === CUSTOM_UNIT) {
                        // Keep a unit already typed; otherwise start blank for typing.
                        return { ...row, _customUnit: true, unit: isCustomUnit(row) ? row.unit : '' };
                      }
                      return { ...row, _customUnit: false, unit: v };
                    }));
                  }}
                >
                  <MenuItem value="">{t('quoteForm.items.noUnit')}</MenuItem>
                  {UNIT_OPTIONS.map(u => (
                    <MenuItem key={u} value={u}>{t(`quoteForm.items.units.${u}`, { defaultValue: u })}</MenuItem>
                  ))}
                  <MenuItem value={CUSTOM_UNIT}>{t('quoteForm.items.units.custom', 'Custom…')}</MenuItem>
                </TextField>
                {isCustomUnit(it) && (
                  <TextField
                    fullWidth
                    size="small"
                    autoFocus={!it.unit}
                    sx={{ mt: 1 }}
                    label={t('quoteForm.items.customUnit', 'Custom unit')}
                    value={it.unit || ''}
                    inputProps={{ maxLength: 30 }}
                    onChange={e => handleItemChange(idx, 'unit', e.target.value)}
                  />
                )}
              </Grid>
              <Grid size={{ xs: 4, sm: 3 }}>
                <TextField fullWidth type="number" label={t('quoteForm.items.unitPrice')} value={it.unit_price} onChange={e => handleItemChange(idx, 'unit_price', e.target.value)} />
              </Grid>
              <Grid size={{ xs: 6, sm: 2 }}>
                <TextField fullWidth type="number" label={t('quoteForm.items.discount')} value={it.discount_percent} onChange={e => handleItemChange(idx, 'discount_percent', e.target.value)} />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <TextField
                  fullWidth
                  label={t('quoteForm.items.amount')}
                  value={formatCurrencyWithSymbol(lineAmount(it))}
                  InputProps={{ readOnly: true }}
                  inputProps={{ style: { textAlign: 'right' } }}
                />
              </Grid>
            </Grid>
          ))}
          <Button variant="outlined" startIcon={<Add />} onClick={addItem}>{t('quoteForm.items.add')}</Button>
        </Box>

        {/* Section: Summary */}
        <Box sx={{ p: 2, mb: 2, borderRadius: 2, border: '1px solid #eee', background: '#fff' }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel id="status-label">Status</InputLabel>
                <Select labelId="status-label" label="Status" value={formData.status} onChange={e => handleChange('status', e.target.value)}>
                  {lookups.statuses.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth label="Total" value={formatCurrencyWithSymbol(subtotal)} InputProps={{ readOnly: true }} />
            </Grid>
          </Grid>
        </Box>

        {/* Site Diagram Upload - Property Layout */}
        {quote && quote.id && (
          <Box sx={{ p: 2, mb: 2, borderRadius: 2, border: '1px solid #eee', background: '#fff' }}>
            <SiteDiagramUpload quoteId={quote.id} readOnly={false} />
          </Box>
        )}

        <Box display="flex" gap={1} justifyContent="flex-end">
          <Button onClick={onCancel} startIcon={<Cancel />}>Cancel</Button>
          <Button variant="contained" onClick={onSubmit} startIcon={<Save />} disabled={loading}>{quote ? 'Save' : 'Create'}</Button>
        </Box>
      </Box>
    </Box>
  );
};

export default QuoteForm;


